import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, CheckCircle2, XCircle, AlertTriangle, Zap } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { getErrorMessage } from '../../lib/errors';


export function EBConnectDryRunPanel() {
  const [loading, setLoading] = useState(false);
  const [institutionId, setInstitutionId] = useState('eu-eb-bnp-fr');
  const [psuType, setPsuType] = useState('personal');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const runDryRun = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const { data } = await api.post('/admin/diagnostics/eb-connect-dry-run', null, {
        params: { institution_id: institutionId, psu_type: psuType, environment: 'production' },
      });
      setResult(data);
      if (data.pass) {
        toast.success(`EB connect dry-run passed for ${institutionId} (${psuType})`);
      } else {
        toast.error(`EB connect dry-run failed: ${data.error || 'unknown'}`);
      }
    } catch (err) {
      const msg = getErrorMessage(err);
      setError(msg);
      setResult({ pass: false, error: msg });
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Zap className="h-5 w-5" />
          EB Connect Dry-Run
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Calls EB POST /auth with the pinned redirect_url and a throwaway state.
          Reports whether an auth URL is received, without redirecting. This proves
          the connect path works end-to-end against the live EB API.
        </p>

        <div className="flex items-center gap-3 flex-wrap">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Institution</label>
            <select
              className="border rounded-md px-3 py-2 text-xs bg-muted/20 focus:outline-none focus:ring-2 focus:ring-ring"
              value={institutionId}
              onChange={(e) => setInstitutionId(e.target.value)}
            >
              <option value="eu-eb-bnp-fr">BNP Paribas (FR)</option>
            </select>
          </div>

          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">PSU Type</label>
            <select
              className="border rounded-md px-3 py-2 text-xs bg-muted/20 focus:outline-none focus:ring-2 focus:ring-ring"
              value={psuType}
              onChange={(e) => setPsuType(e.target.value)}
            >
              <option value="personal">Personal</option>
              <option value="business">Business</option>
            </select>
          </div>

          <div className="pt-4">
            <Button size="sm" onClick={runDryRun} disabled={loading}>
              {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
              Dry-Run Connect
            </Button>
          </div>
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Running EB connect dry-run...
          </div>
        )}

        {!loading && result && (
          <div className="border rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-3">
              {result.pass ? (
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              ) : (
                <XCircle className="h-5 w-5 text-red-500" />
              )}
              <Badge
                variant={result.pass ? 'default' : 'destructive'}
                className={result.pass ? 'bg-green-600 hover:bg-green-700 text-[11px]' : 'text-[11px]'}
              >
                {result.pass ? 'PASS' : 'FAIL'}
              </Badge>
              {result.eb_status && (
                <span className="text-xs text-muted-foreground">
                  EB HTTP {result.eb_status}
                </span>
              )}
            </div>

            {result.pass && result.auth_url_received && (
              <div className="rounded border px-3 py-2 bg-green-50">
                <p className="text-xs font-medium text-green-800 mb-1">Auth URL received</p>
                <p className="text-[10px] text-green-700 break-all">{result.auth_url_received}</p>
              </div>
            )}

            {!result.pass && result.error && (
              <div className="flex items-start gap-2 text-sm text-red-600">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span className="text-xs">{result.error}</span>
              </div>
            )}

            {!result.pass && result.eb_body && (
              <div className="rounded border px-3 py-2 bg-muted/20">
                <p className="text-xs font-medium text-muted-foreground mb-1">EB Response Body</p>
                <p className="text-[10px] break-all">{result.eb_body}</p>
              </div>
            )}

            {result.redirect_url_used && (
              <p className="text-[10px] text-muted-foreground">
                Redirect URL: <span className="font-mono">{result.redirect_url_used}</span>
              </p>
            )}

            {result.institution_name && (
              <p className="text-xs text-muted-foreground">
                Institution: {result.institution_name} ({result.psu_type})
              </p>
            )}
          </div>
        )}

        {!loading && error && !result && (
          <div className="flex items-center gap-2 text-sm text-red-500">
            <AlertTriangle className="h-4 w-4" />
            {error}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
